import { useCallback, useRef } from 'react';
import {
    createAIConversation,
    aiChatMessageRequestCreate,
    aiChatMessageRequestPoll,
    fetchProjectAIConfig,
} from '../../../utils/api';

const POLL_INITIAL_WAIT_MS = 8000;   // AI は 10〜15 秒かかるため最初は長めに待つ
const POLL_INTERVAL_MS = 2000;       // その後は 2 秒間隔
const POLL_MAX_WAIT_MS = 60000;     // 最大 1 分で諦める

/** AbortSignal に対応した sleep */
const sleep = (ms, signal) => new Promise((resolve, reject) => {
    const timer = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => {
        clearTimeout(timer);
        reject(new DOMException('Aborted', 'AbortError'));
    }, { once: true });
});

/**
 * AIチャットメッセージ送信フック（非同期: POST .../projects/<id>/messages/ → 202 + ポーリング）
 */
export function useAIChatSend(
    projectId,
    conversationId,
    setConversationId,
    messages,
    setMessages,
    setInput,
    setPendingInput,
    setLoading,
    setShowLoadingIndicator,
    setError,
    messagesRef,
    requestAbortRef,
    maxTurns,
    textareaRef,
    loading,
    purging,
    aiConfig = null,
    isWsConnected = false,
    waitForResult = null,
    reconnectWs = null
) {
    const requestGenRef = useRef(0);

    const getErrorMessage = useCallback(async (err) => {
        let errorMessage = "送信に失敗しました。";
        try {
            const errorData = await err?.response?.json?.() || err?.response?.data;
            const errorCode = errorData?.error;
            if (errorCode === 'ai_config_not_found') {
                errorMessage = "このプロジェクトにはAI設定がありません。管理者に問い合わせてください。";
            } else if (errorCode === 'ai_disabled') {
                errorMessage = "このプロジェクトのAI機能は無効です。管理者に問い合わせてください。";
            } else if (errorCode === 'api_key_not_configured') {
                errorMessage = "AI機能の設定が完了していません。管理者に問い合わせてください。";
            } else if (errorCode === 'turn_limit_exceeded') {
                errorMessage = errorData?.message || "最大ターン数に達しました。これ以上の会話はできません。";
            } else if (errorCode === 'conversation_not_found') {
                errorMessage = "会話が見つかりません。";
            } else if (errorCode === 'conversation_model_mismatch') {
                errorMessage = errorData?.message || "会話のモデルと現在の設定が一致しません。新しい会話を開始してください。";
            } else if (errorCode === 'model_required') {
                errorMessage = errorData?.message || "model を指定してください。";
            } else if (errorData?.error_message) {
                errorMessage = errorData.error_message;
            } else if (errorData?.message) {
                errorMessage = errorData.message;
            }
        } catch (_) {
            // ignore
        }
        return errorMessage;
    }, []);

    const calculateTurn = useCallback((targetTurn, currentMessages) => {
        let turn = targetTurn;
        if (turn === null || turn === undefined) {
            const userMessages = currentMessages.filter(m => m.role === "user");
            turn = userMessages.length + 1;
        }
        if (turn > maxTurns) turn = maxTurns;
        if (turn < 1) turn = 1;
        return Number(turn);
    }, [maxTurns]);

    const sendMessageDirectly = useCallback(async (content, targetTurn = null, baseMessages = null) => {
        if (!content.trim() || loading || purging) return;
        setError("");

        // 送信直前に最新の AI 設定を確認する。
        // 管理者が設定を変更するとバックエンドは会話履歴を削除するため、
        // 古い設定・会話 ID のまま送ると 404 等で不可解にリセットされる。
        // 変更を検知したら送信を中止し、通知のうえ最新状態で再読み込みする（バックエンドの削除挙動と整合）。
        if (projectId && aiConfig) {
            try {
                const latest = await fetchProjectAIConfig(projectId);
                const changed = !!latest && (
                    latest.model_type !== aiConfig.model_type ||
                    latest.updated_at !== aiConfig.updated_at
                );
                if (changed) {
                    setError("AI設定が変更されました。最新の設定で再読み込みします。");
                    setTimeout(() => {
                        try { window.location.reload(); } catch (_) { /* noop */ }
                    }, 1500);
                    return;
                }
            } catch (_) {
                // 設定確認に失敗した場合は通常どおり送信を続行する
            }
        }

        const myGen = ++requestGenRef.current;
        // baseMessages が渡された場合（編集再送）はそれを基準にする。
        // messagesRef の更新タイミング（再レンダリング）に依存しないため setTimeout レースを回避できる。
        const currentMessages = baseMessages ?? messagesRef.current;
        const turn = calculateTurn(targetTurn, currentMessages);

        // プレースホルダは一意 ID で識別し、完了時の差し替え・失敗時の除去をこの ID で行う
        // （turn 一致での特定はやめる。失敗→同ターン再送時の取り違えを防ぐ）
        const rand = Math.random().toString(36).substr(2, 9);
        const userMessageId = `temp-user-${Date.now()}-${rand}`;
        const assistantMessageId = `temp-assistant-${Date.now()}-${rand}`;
        const userMessage = {
            role: "user",
            content: content,
            id: userMessageId,
            created_at: new Date().toISOString(),
            turn: turn
        };
        const assistantPlaceholder = {
            role: "assistant",
            content: "",
            id: assistantMessageId,
            created_at: new Date().toISOString(),
            turn: turn
        };
        if (baseMessages) {
            // 編集再送: 基準配列を確定的に置き換える（prev に依存しない）
            setMessages([...baseMessages, userMessage, assistantPlaceholder]);
        } else {
            setMessages(prev => [...prev, userMessage, assistantPlaceholder]);
        }

        // 失敗・中断時に空のアシスタントプレースホルダを除去するヘルパー
        const removeAssistantPlaceholder = () => {
            setMessages(prev => prev.filter(
                m => !(m.id === assistantMessageId && m.role === "assistant" && !m.content)
            ));
        };

        setInput("");
        setPendingInput("");
        setLoading(true);
        setShowLoadingIndicator(true);

        const controller = new AbortController();
        requestAbortRef.current = controller;

        try {
            if (!projectId) {
                setError("プロジェクトIDが取得できませんでした。");
                setLoading(false);
                return;
            }
            let effectiveConvId = conversationId;
            if (!effectiveConvId) {
                const isDisasterModel = aiConfig?.function_model_id === 'disaster_tankyu' || aiConfig?.function_model_id === 'disaster_bigbrother';
                const defaultTsunami = (isDisasterModel || !aiConfig) ? '津波なし' : undefined;
                const defaultTurnCounter = (isDisasterModel || !aiConfig) ? '8ターン' : undefined;
                const createPayload = {
                    age: aiConfig?.default_age || '高学年',
                    tsunami: aiConfig?.default_tsunami ?? defaultTsunami,
                    turn_counter: aiConfig?.function_model_id === 'disaster_tankyu'
                        ? '8ターン'
                        : (aiConfig?.function_model_id === 'disaster_bigbrother' ? (aiConfig?.default_turn_counter || '8ターン') : (aiConfig?.default_turn_counter ?? defaultTurnCounter)),
                };
                // 災害種別（防災シミュレーション・防災リサーチのみ）を会話作成時にスナップショット
                if (isDisasterModel && aiConfig?.default_disaster_type) {
                    createPayload.disaster_type = aiConfig.default_disaster_type;
                }
                const created = await createAIConversation(projectId, createPayload);
                effectiveConvId = created?.external_conversation_id || created?.id;
                if (effectiveConvId) setConversationId(String(effectiveConvId));
            }
            if (!effectiveConvId) {
                setError("会話の作成に失敗しました。");
                setLoading(false);
                return;
            }
            // 1. WS 切断中なら POST の前に再接続する
            //    （ジョブは作成時点の User.ws_connection_id をスナップショットするため、
            //      先に再接続して connection_id を更新しておく必要がある）
            let wsAvailable = isWsConnected;
            if (!wsAvailable && reconnectWs) {
                wsAvailable = await reconnectWs();
            }
            if (myGen !== requestGenRef.current) return;

            // 2. 非同期エンドポイントへ POST → 202 + job_id
            const { id: jobId } = await aiChatMessageRequestCreate(
                projectId,
                { query: content, conversation_id: effectiveConvId, turn },
                controller.signal
            );
            if (myGen !== requestGenRef.current) return;

            // 3a. WS 接続済み（または再接続成功）→ WS で結果を待つ（中断対応）
            if (wsAvailable && waitForResult) {
                const wsResult = await Promise.race([
                    waitForResult(jobId, POLL_MAX_WAIT_MS),
                    new Promise((_, reject) => {
                        if (controller.signal.aborted) reject(new DOMException('Aborted', 'AbortError'));
                        controller.signal.addEventListener('abort', () => reject(new DOMException('Aborted', 'AbortError')), { once: true });
                    }),
                ]);
                if (myGen !== requestGenRef.current) return;

                if (wsResult) {
                    // WS で結果を受信
                    if (wsResult.status === 'completed') {
                        setMessages(prev => {
                            const copy = [...prev];
                            const idx = copy.findIndex(m => m.id === assistantMessageId);
                            if (idx >= 0) {
                                copy[idx] = { ...copy[idx], content: wsResult.answer ?? '', id: wsResult.message_id || copy[idx].id, metadata: wsResult.metadata };
                            }
                            return copy;
                        });
                        return;
                    } else if (wsResult.status === 'failed') {
                        throw Object.assign(new Error(wsResult.error_message || '処理に失敗しました'), {
                            response: { data: { error: wsResult.error_code, message: wsResult.error_message } }
                        });
                    }
                }
                // WS タイムアウト → ポーリングへフォールバック
            }

            // 3b. ポーリング（WS 未接続 or WS タイムアウト時のフォールバック）
            await sleep(POLL_INITIAL_WAIT_MS, controller.signal);
            if (myGen !== requestGenRef.current) return;

            const deadline = Date.now() + POLL_MAX_WAIT_MS;
            while (true) {
                if (controller.signal.aborted) throw new DOMException('Aborted', 'AbortError');
                const poll = await aiChatMessageRequestPoll(projectId, jobId, controller.signal);
                if (myGen !== requestGenRef.current) return;

                if (poll.status === 'completed') {
                    setMessages(prev => {
                        const copy = [...prev];
                        const idx = copy.findIndex(m => m.id === assistantMessageId);
                        if (idx >= 0) {
                            copy[idx] = { ...copy[idx], content: poll.answer ?? '', id: poll.message_id || copy[idx].id, metadata: poll.metadata };
                        }
                        return copy;
                    });
                    break;
                } else if (poll.status === 'failed') {
                    throw Object.assign(new Error(poll.error_message || '処理に失敗しました'), {
                        response: { data: { error: poll.error_code, message: poll.error_message } }
                    });
                } else if (Date.now() > deadline) {
                    throw Object.assign(new Error('処理がタイムアウトしました'), {
                        response: { data: { error: 'timeout' } }
                    });
                }
                await sleep(POLL_INTERVAL_MS, controller.signal);
            }
        } catch (err) {
            // 失敗・中断いずれの場合も空アシスタントプレースホルダを除去する
            // （ユーザー発言は残す。残置すると同ターン再送時の取り違え・孤児プレースホルダの原因になる）
            removeAssistantPlaceholder();
            if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED' || err?.name === 'AbortError') {
                // noop
            } else {
                setError(await getErrorMessage(err));
            }
        } finally {
            requestAbortRef.current = null;
            setLoading(false);
            setShowLoadingIndicator(false);
            if (textareaRef?.current) textareaRef.current.style.height = "auto";
        }
    }, [
        projectId, conversationId, setConversationId, setMessages, setInput, setPendingInput,
        setLoading, setShowLoadingIndicator, setError,
        messagesRef, requestAbortRef, calculateTurn, getErrorMessage,
        aiConfig, textareaRef, loading, purging, isWsConnected, waitForResult, reconnectWs
    ]);

    const sendMessage = useCallback(async (targetTurn = null, pendingInputValue) => {
        const content = pendingInputValue ?? messagesRef.current.filter(m => m.role === "user").slice(-1)[0]?.content;
        if (!content?.trim() || loading || purging) return;
        await sendMessageDirectly(content, targetTurn ?? null);
    }, [sendMessageDirectly, messagesRef, loading, purging]);

    return {
        sendMessage,
        sendMessageDirectly,
        requestGenRef
    };
}
